import type { CreateEntityTokenResponse } from '@onefootprint/request-types/dashboard';
import { useTranslation } from 'react-i18next';
import useEntity from '@/entity/hooks/use-entity';
import hasDataIdentifier from '@/utils/has-data-identifier';
import useSendTokenLinkMutation from '../../../../../hooks/use-send-token-link';
import LinkDisplay from '../../../../link-display';

type UseDisplayLinkDialogProps = {
  linkData?: Pick<CreateEntityTokenResponse, 'link' | 'expiresAt'>;
  onClose: () => void;
  entityId: string;
};

const useDisplayLinkDialog = ({ linkData, onClose, entityId }: UseDisplayLinkDialogProps) => {
  const sendSmsMutation = useSendTokenLinkMutation(entityId);
  const sendEmailMutation = useSendTokenLinkMutation(entityId);
  const { t } = useTranslation('entity-details', {
    keyPrefix: 'actions.requestMoreInfo',
  });
  const entity = useEntity(entityId)?.data;
  const userHasPhone = hasDataIdentifier(entity, 'id.phone_number');
  const userHasEmail = hasDataIdentifier(entity, 'id.email');

  const component = linkData && (
    <LinkDisplay
      title={t($ => $.link.headerGenerated)}
      description={t($ => $.link.shareThisLink)}
      linkData={linkData}
    />
  );

  const buttonsDisabled = !linkData || sendSmsMutation.isPending || sendEmailMutation.isPending;
  const primaryButton = userHasPhone
    ? {
        label: t($ => $.link.sendLinkSms),
        disabled: buttonsDisabled,
        loading: sendSmsMutation.isPending,
        onClick: () => sendSmsMutation.mutate({ kind: 'inherit', sendTo: 'phone', onDone: onClose }),
      }
    : undefined;
  const secondaryButton = userHasEmail
    ? {
        label: t($ => $.link.sendLinkEmail),
        disabled: buttonsDisabled,
        loading: sendEmailMutation.isPending,
        onClick: () => sendEmailMutation.mutate({ kind: 'inherit', sendTo: 'email', onDone: onClose }),
      }
    : undefined;

  return {
    title: t($ => $.title),
    primaryButton,
    secondaryButton,
    component,
  };
};

export default useDisplayLinkDialog;
